import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Database, MagnifyingGlass, SignOut, User, ShieldCheck, CaretDown, CaretUp, PencilSimple, Spinner, Funnel, ArrowLeft, X, Phone, Envelope, MapPin, CalendarBlank, FileText, Receipt, File as FileIcon, VideoCamera, ChatCircle, BookOpen, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { checkDbStatus, fetchCustomers, fetchCustomerPersons, fetchCustomerQuotes, fetchQuote, updateQuoteHeading, addQuoteLine, updateQuoteLine, deleteQuoteLine, searchParts,
  fetchCustomerCompressors, fetchCustomerPartsPurchased, fetchCustomerJournal, fetchCustomerNotes,
  fetchCustomerPhoneLogs,
} from "@/lib/api";
import CompressorDetailModal from "@/components/CompressorDetailModal";
import AddCustomerModal from "@/components/AddCustomerModal";
import VideoCallModal from "@/components/VideoCallModal";
import SmsChatPanel from "@/components/SmsChatPanel";
import SendPublicFilesModal from "@/components/SendPublicFilesModal";
import { customerTypeIcon } from "@/components/CustomerTypeIcons";
import { NavLink, useNavigate } from "react-router-dom";
import WavingFlag from "@/components/WavingFlag";
import RecordButton from "@/components/RecordButton";
import CustomerRecordingsSection from "@/components/CustomerRecordingsSection";
import AdminPanel from "@/components/AdminPanel";
import RotorIcon from "@/components/RotorIcon";
import PartFilesModal from "@/components/PartFilesModal";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";
import { useT } from "@/lib/i18n";

const COUNTRY_COLOR = { US: "#1D4ED8", CA: "#D80621", Other: "#475569" };

const COUNTRY_CONFIG = {
  US: { label: "US Customers", flag: "https://flagcdn.com/w320/us.png", accent: "text-blue-700", accentBg: "bg-blue-50", accentBorder: "border-blue-100", groupText: "text-blue-900", badge: "text-blue-500 bg-blue-100", hoverRow: "hover:bg-blue-50/70", oddBg: "bg-blue-50/40", oddSticky: "bg-blue-50", tabActive: "border-blue-700 text-blue-700", detailAccent: "bg-blue-700", cardBorder: "border-blue-200" },
  CA: { label: "Canadian Customers", flag: "https://flagcdn.com/w320/ca.png", accent: "text-[#D80621]", accentBg: "bg-red-50", accentBorder: "border-red-100", groupText: "text-red-900", badge: "text-red-600 bg-red-100", hoverRow: "hover:bg-red-50/50", oddBg: "bg-red-50/50", oddSticky: "bg-red-50", tabActive: "border-[#D80621] text-[#D80621]", detailAccent: "bg-[#D80621]", cardBorder: "border-red-200" },
  Other: { label: "Other Countries", flag: null, accent: "text-slate-700", accentBg: "bg-slate-50", accentBorder: "border-slate-200", groupText: "text-slate-900", badge: "text-slate-500 bg-slate-200", hoverRow: "hover:bg-slate-50/50", oddBg: "bg-slate-100/70", oddSticky: "bg-slate-100", tabActive: "border-slate-700 text-slate-700", detailAccent: "bg-slate-700", cardBorder: "border-slate-300" },
};

const COLUMNS = [
  { key: "Company Name", label: "Company Name", defW: 220, align: "left", sticky: true, filterType: "text" },
  { key: "Type", label: "Type", defW: 110, align: "left", filterType: "text" },
  // req #12 (iter110): Avg Quote / Credit / Debit / Currency are derived
  // financial roll-ups stored across multiple tables and not directly
  // sortable/searchable on the MySQL side without expensive joins. Per
  // the customer's request, leave them as read-only display columns —
  // no sort header, no filter input.
  { key: "avg_quote", label: "Avg Quote", defW: 110, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Credit", label: "Credit", defW: 90, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Debit", label: "Debit", defW: 90, align: "right", format: "money", sortable: false, filterType: null },
  { key: "Currency", label: "Currency", defW: 75, align: "left", sortable: false, filterType: null },
  { key: "Answered By", label: "Answered By", defW: 160, align: "left", filterType: "text" },
  { key: "Bill to Address", label: "Bill to Address", defW: 280, align: "left", filterType: "text" },
  { key: "Segment", label: "Segment", defW: 150, align: "left", filterType: "text" },
  { key: "Contact Date", label: "Contact Date", defW: 130, align: "left", filterType: null, formatDate: true },
  { key: "Person(1)", label: "Contact", defW: 140, align: "left", filterType: "text" },
  { key: "Email(1)", label: "Email", defW: 180, align: "left", filterType: "text" },
  { key: "TelePhone(1)", label: "Phone", defW: 120, align: "left", filterType: null },
  { key: "Compressor_Owner", label: "Compressor", defW: 100, align: "left", filterType: "text" },
  { key: "_Notes", label: "Notes", defW: 200, align: "left", filterType: null },
];

const SUPER_EDITOR_EMAILS = ["bora@usaircompressor.com", "deniz@usaircompressor.com", "jan@usaircompressor.com"];

function formatCell(value, format, col) {
  if (value == null || value === "" || value === "None") return "-";
  if (format === "money" && !isNaN(value)) return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  if (col?.formatDate && value) {
    try { return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return String(value); }
  }
  const str = String(value);
  return str.length > 100 ? str.slice(0, 100) + "..." : str;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function getDayOfWeek(d) { if (!d) return ""; try { return DAY_NAMES[new Date(d).getDay()] || ""; } catch { return ""; } }
// Sort priority within a week: Friday first, then Thu, Wed, Tue, Mon, then weekend last.
// JS getDay(): Sun=0 Mon=1 Tue=2 Wed=3 Thu=4 Fri=5 Sat=6
const DAY_SORT_RANK = { 5: 0, 4: 1, 3: 2, 2: 3, 1: 4, 6: 5, 0: 6 };
function getDaySortRank(d) {
  if (!d) return 99;
  try { return DAY_SORT_RANK[new Date(d).getDay()] ?? 99; } catch { return 99; }
}
const DAY_BADGE_STYLE = {
  Fri: "bg-amber-100 text-amber-800 border-amber-200",
  Thu: "bg-sky-100 text-sky-800 border-sky-200",
  Wed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Tue: "bg-indigo-100 text-indigo-800 border-indigo-200",
  Mon: "bg-slate-200 text-slate-700 border-slate-300",
  Sat: "bg-rose-100 text-rose-700 border-rose-200",
  Sun: "bg-rose-100 text-rose-700 border-rose-200",
};

// Normalize a phone number to an E.164-ish tel: URI so iOS/Android reliably dial.
// - Keeps leading `+` if present
// - Prepends `+1` for 10-digit North American numbers
// - Converts leading `1` + 10 digits to `+1...`
// - Appends extension via `,` pause DTMF (iOS) only when provided
function toTelHref(raw, ext) {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[^+0-9]/g, "");
  let num;
  if (cleaned.startsWith("+")) num = cleaned;
  else if (cleaned.length === 10) num = `+1${cleaned}`;
  else if (cleaned.length === 11 && cleaned.startsWith("1")) num = `+${cleaned}`;
  else num = cleaned;
  const extPart = ext ? `,${String(ext).replace(/[^0-9]/g, "")}` : "";
  return `tel:${num}${extPart}`;
}

function formatYearWeek(yw) {
  if (!yw || yw === 0) return "No Contact Date";
  const s = String(yw);
  if (s.length < 5) return `Week ${s}`;
  const year = parseInt(s.slice(0, 4), 10);
  const week = parseInt(s.slice(4), 10);
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${year} — Week ${week}  (${fmt(monday)} – ${fmt(sunday)})`;
}

export default function CustomerPage({ user, onLogout, country }) {
  const cfg = COUNTRY_CONFIG[country] || COUNTRY_CONFIG.US;
  const navigate = useNavigate();
  const t = useT();
  const [dbConnected, setDbConnected] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ─── Recently-viewed customers (per-user, persisted in localStorage) ───
  // Capped at 5; newest first; updated whenever a detail panel opens.
  const RECENT_KEY = `usair_recent_customers_${(user?.email || "anon").toLowerCase()}`;
  const RECENT_MAX = 5;
  const [recentCustomers, setRecentCustomers] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw).slice(0, RECENT_MAX) : [];
    } catch { return []; }
  });
  const pushRecentCustomer = useCallback((c) => {
    if (!c?.CustomerID) return;
    const entry = {
      CustomerID: c.CustomerID,
      "Company Name": c["Company Name"] || "",
      Type: c.Type || "",
      addr_country: c.addr_country || "",
      ts: Date.now(),
    };
    setRecentCustomers((prev) => {
      const filtered = (prev || []).filter((p) => p.CustomerID !== entry.CustomerID);
      const next = [entry, ...filtered].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* quota exceeded — ignore */ }
      return next;
    });
  }, [RECENT_KEY]);
  const removeRecentCustomer = useCallback((cid) => {
    setRecentCustomers((prev) => {
      const next = (prev || []).filter((p) => p.CustomerID !== cid);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [RECENT_KEY]);
  const openCustomer = useCallback((c) => {
    setSelectedCustomer(c);
    pushRecentCustomer(c);
  }, [pushRecentCustomer]);

  // Deep-link support (iter111 — req #1): the Compressor Detail modal's
  // owner-company link routes to this page with `?focus=<CustomerID>`.
  // Once the customer list has loaded, find the matching row and open
  // its detail panel automatically. Removes the param afterwards so a
  // back-button from inside the modal doesn't re-trigger.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (!focus) return;
    if (loading) return;
    focusedRef.current = true;
    const list = data?.items || [];
    const match = list.find((c) => c.CustomerID === focus);
    if (match) {
      openCustomer(match);
    } else {
      // Couldn't find it on the current page (might be on a different
      // country segment or filtered out). Open with a stub so the
      // detail modal still loads — its sub-fetches use CustomerID only.
      openCustomer({ CustomerID: focus, "Company Name": "" });
    }
    params.delete("focus");
    const newQs = params.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (newQs ? `?${newQs}` : ""));
  }, [loading, data, openCustomer]);

  useEffect(() => { checkDbStatus().then((d) => setDbConnected(d.connected)).catch(() => setDbConnected(false)); }, []);
  // Each load() call gets a monotonically increasing request id. When a
  // response comes back we ignore it unless its id is still the latest one
  // we asked for. This kills the rapid-typing race where short queries
  // (broad result set, slow response) used to overwrite long queries
  // (narrow result set, fast response) and revert the list to "almost full".
  const latestReqIdRef = useRef(0);
  const load = useCallback(() => {
    const myId = ++latestReqIdRef.current;
    setLoading(true);
    fetchCustomers({ country, per_page: 300, search: globalSearch })
      .then((d) => { if (myId === latestReqIdRef.current) setData(d); })
      .catch((e) => { if (myId === latestReqIdRef.current) console.error(e); })
      .finally(() => { if (myId === latestReqIdRef.current) setLoading(false); });
  }, [country, globalSearch]);
  useEffect(() => { load(); }, [load]);

  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Column sort — overrides the default (day-of-week → Company Name) ordering within each week group.
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const handleSort = (col) => {
    if (sortKey !== col) { setSortKey(col); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(""); setSortDir("asc"); // third click clears → default
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((item) => {
      for (const col of COLUMNS) {
        const f = filters[col.key];
        if (!f || !f.value) continue;
        const val = String(item[col.key] || "").toLowerCase();
        if (!val.includes(f.value.toLowerCase())) return false;
      }
      return true;
    });
  }, [data?.items, filters]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of filteredItems) {
      const yw = item.Contact_Year_Week || 0;
      if (!map.has(yw)) map.set(yw, []);
      map.get(yw).push(item);
    }
    // Custom sort overrides the default day-of-week → company order within each week group.
    const customSort = sortKey ? (a, b) => {
      const col = COLUMNS.find((c) => c.key === sortKey);
      const accessor = sortKey === "Answered By" ? ((r) => r["Answered By Name"] || r["Answered By"] || "")
        : (col?.format === "money" ? ((r) => Number(r[sortKey]) || 0)
        : ((r) => r[sortKey]));
      const va = accessor(a), vb = accessor(b);
      const na = va == null || va === ""; const nb = vb == null || vb === "";
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === "number" && typeof vb === "number") return (sortDir === "desc" ? -1 : 1) * (va - vb);
      return (sortDir === "desc" ? -1 : 1) * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    } : null;
    for (const items of map.values()) {
      if (customSort) {
        items.sort(customSort);
      } else {
        items.sort((a, b) => {
          const ra = getDaySortRank(a["Contact Date"]);
          const rb = getDaySortRank(b["Contact Date"]);
          if (ra !== rb) return ra - rb;
          return String(a["Company Name"] || "").localeCompare(String(b["Company Name"] || ""));
        });
      }
    }
    return Array.from(map.entries()).map(([yw, items]) => ({ key: yw, label: formatYearWeek(yw), items }));
  }, [filteredItems, sortKey, sortDir]);

  const toggleGroup = (key) => setCollapsedGroups((p) => ({ ...p, [key]: !p[key] }));
  const updateFilter = (key, value) => setFilters((p) => ({ ...p, [key]: { value } }));
  const clearFilters = () => setFilters({});
  const activeFilterCount = Object.values(filters).filter((f) => f.value && f.value !== "").length;

  const userEmail = (user?.email || "").toLowerCase();
  const isSuperEditor = SUPER_EDITOR_EMAILS.includes(userEmail);
  const hasEditColumn = user?.is_admin || isSuperEditor;
  const isCA = country === "CA";
  const colCount = COLUMNS.length + (hasEditColumn ? 1 : 0);
  const tableMinWidth = COLUMNS.reduce((s, c) => s + c.defW, 0) + (hasEditColumn ? 40 : 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"><ArrowLeft size={18} weight="bold" /></button>
          <Database size={20} weight="bold" className="text-blue-700 hidden md:block" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo'] mr-2">{t("customer.page_brand", "Compressors R Us")}</h1>
          <div className="flex-1 max-w-md relative hidden md:block">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customer-search-input" placeholder={t("customer.search_placeholder", "Search customers...")}
              className="pl-9 h-9 rounded-sm border-slate-300 text-sm" value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {user?.is_admin && <button onClick={() => setShowAdmin(true)} className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-500"><ShieldCheck size={14} weight="bold" /></button>}
            <span className="text-xs text-slate-500 hidden md:inline truncate max-w-[100px]">{user?.email || "Guest"}</span>
            <button onClick={onLogout} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400"><SignOut size={14} /></button>
          </div>
        </div>
        {/* Mobile search */}
        <div className="md:hidden px-4 pb-2">
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder={t("customer.search_placeholder_short", "Search...")} className="pl-8 h-8 rounded-sm border-slate-300 text-sm"
              value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-3 md:p-8">
        {/* Tabs */}
        <div className="flex items-center gap-0.5 mb-4 md:mb-6 border-b border-slate-200 overflow-x-auto">
          {Object.entries(COUNTRY_CONFIG).map(([key, c]) => (
            <NavLink key={key} to={`/customers-${key.toLowerCase()}`} data-testid={`tab-customers-${key.toLowerCase()}`}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${country === key ? c.tabActive : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {c.label}
            </NavLink>
          ))}
        </div>

        {/* Recently viewed strip — last 5 customers opened by this user.
            Tapped chip reopens the detail panel; small × removes the entry. */}
        {recentCustomers.length > 0 && (
          <div className="mb-3" data-testid="recent-customers-strip">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mr-1">
                {t("customer.recently_viewed", "Recently viewed")}
              </span>
              {recentCustomers.map((rc) => {
                const tIcon = customerTypeIcon(rc.Type);
                return (
                  <div key={rc.CustomerID} data-testid={`recent-chip-${rc.CustomerID}`}
                    className={`group inline-flex items-center gap-1.5 pl-2 pr-1 h-7 rounded-full border ${isCA ? "border-red-200 bg-red-50/60 hover:bg-red-100" : "border-blue-200 bg-blue-50/60 hover:bg-blue-100"} transition-colors cursor-pointer`}
                    onClick={() => openCustomer({ CustomerID: rc.CustomerID, "Company Name": rc["Company Name"] })}
                    title={`Reopen ${rc["Company Name"] || rc.CustomerID}`}>
                    {tIcon ? <tIcon.Icon size={12} className={`${tIcon.color} shrink-0`} />
                      : <User size={12} weight="fill" className={`${cfg.accent} shrink-0`} />}
                    <span className="text-[11px] font-semibold text-slate-700 max-w-[180px] truncate">
                      {rc["Company Name"] || rc.CustomerID}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); removeRecentCustomer(rc.CustomerID); }}
                      data-testid={`recent-remove-${rc.CustomerID}`}
                      className="w-4 h-4 rounded-full text-slate-400 hover:text-rose-600 hover:bg-white/60 flex items-center justify-center"
                      title={t("customer.remove_recent", "Remove from recent")}>
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Count + Flag + Filters */}
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span data-testid="customer-total-count" className={`text-xs font-bold tracking-[0.15em] whitespace-nowrap ${cfg.accent}`}>
              {filteredItems.length}/{data?.total || 0} {cfg.label}
            </span>
            {cfg.flag && <div className="hidden md:block"><WavingFlag src={cfg.flag} width={90} height={56} speed={0.8} /></div>}
            {cfg.flag && <div className="md:hidden"><WavingFlag src={cfg.flag} width={50} height={32} speed={0.8} /></div>}
            <button data-testid="toggle-customer-filters" onClick={() => setShowFilters(!showFilters)}
              className={`text-xs font-semibold px-2 py-1 rounded-sm flex items-center gap-1 border ${
                showFilters || activeFilterCount > 0 ? cfg.accent + " " + cfg.accentBg + " " + cfg.accentBorder : "text-slate-500 bg-white border-slate-200"
              }`}>
              <Funnel size={12} weight={activeFilterCount > 0 ? "fill" : "bold"} />
              <span className="hidden md:inline">Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}</span>
              {activeFilterCount > 0 && <span className="md:hidden">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-slate-500 underline">{t("customer.clear_filters", "Clear")}</button>}
          </div>
          <button
            type="button"
            data-testid="customer-add-btn"
            onClick={() => setShowAddCustomer(true)}
            className={`text-xs font-bold uppercase tracking-wider h-8 px-3 rounded-sm text-white inline-flex items-center gap-1 ${isCA ? "bg-red-700 hover:bg-red-800" : "bg-blue-700 hover:bg-blue-800"}`}
          >
            {t("customer.add_button", "+ Add customer")}
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* Indeterminate progress bar — visible whenever we're refetching
               (country tab switch, new search, etc.). Without this the user
               sees stale data with no indication a request is in flight. */}
            {loading && (
              <div className="relative h-0.5 bg-slate-100 overflow-hidden mb-2 rounded-sm" data-testid="customer-list-loading-bar">
                <div className={`absolute inset-y-0 w-1/3 ${cfg.detailAccent} animate-loadingbar`} />
              </div>
            )}
            <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
            {/* ═══ MOBILE VIEW ═══ */}
            <div className="md:hidden space-y-1" style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
              {grouped.length === 0 ? (
                <p className="text-center py-12 text-slate-400 text-sm">{t("customer.no_customers", "No customers found")}</p>
              ) : grouped.map(({ key, label, items }) => (
                <div key={key}>
                  {/* Group header — full width, not scrollable */}
                  <button onClick={() => toggleGroup(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 ${cfg.accentBg} ${cfg.accentBorder} border-b`}>
                    {collapsedGroups[key] ? <CaretDown size={13} weight="bold" className={cfg.accent} /> : <CaretUp size={13} weight="bold" className={cfg.accent} />}
                    <span className={`text-xs font-bold font-['Chivo'] ${cfg.groupText}`}>{label}</span>
                    <span className={`text-[10px] font-semibold px-1 py-0.5 rounded-sm ${cfg.badge}`}>{items.length}</span>
                  </button>
                  {/* Customer rows — tap to open detail */}
                  {!collapsedGroups[key] && items.map((cust, idx) => {
                    const isOdd = idx % 2 === 1;
                    const typeIcon = customerTypeIcon(cust.Type);
                    return (
                      <button key={cust.id} data-testid={`mobile-customer-${cust.id}`}
                        onClick={() => openCustomer(cust)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 border-b border-slate-100 text-left transition-colors ${isOdd ? (isCA ? "bg-red-50/40" : "bg-slate-50") : "bg-white"} active:bg-slate-100`}>
                        <span className="flex items-center gap-1.5 truncate flex-1">
                          {typeIcon && <typeIcon.Icon size={14} className={`${typeIcon.color} shrink-0`} />}
                          {cust.Compressor_Owner === "Y" && <RotorIcon size={14} color={COUNTRY_COLOR[country]} className="shrink-0" />}
                          <span className="text-sm font-semibold text-slate-900 truncate">{cust["Company Name"] || "-"}</span>
                          {Number(cust.avg_quote) > 0 && (
                            <span className="text-[10px] font-mono text-slate-500 ml-auto shrink-0">${Number(cust.avg_quote).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                          )}
                        </span>
                        <CaretDown size={12} weight="bold" className="text-slate-300 -rotate-90 ml-2 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* ═══ DESKTOP VIEW ═══ */}
            <div className="hidden md:block border border-slate-200 rounded-sm overflow-y-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
              {/* Group sections with full-width headers + scrollable table rows */}
              {grouped.length === 0 ? (
                <p className="text-center py-12 text-slate-400">{t("customer.no_customers", "No customers found")}</p>
              ) : grouped.map(({ key, label, items }) => (
                <div key={key}>
                  {/* Group header — full width, NOT inside scrollable area */}
                  <button onClick={() => toggleGroup(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 ${cfg.accentBg} ${cfg.accentBorder} border-b cursor-pointer hover:opacity-80`}
                    data-testid={`customer-group-${key}`}>
                    {collapsedGroups[key] ? <CaretDown size={14} weight="bold" className={cfg.accent} /> : <CaretUp size={14} weight="bold" className={cfg.accent} />}
                    <span className={`text-sm font-bold font-['Chivo'] tracking-tight ${cfg.groupText}`}>{label}</span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-sm ${cfg.badge}`}>{items.length}</span>
                  </button>
                  {/* Scrollable table for this group */}
                  {!collapsedGroups[key] && (
                    <div className="overflow-x-scroll" style={{ WebkitOverflowScrolling: "touch" }}>
                      <table className="data-table border-collapse" style={{ tableLayout: "fixed", minWidth: `${tableMinWidth}px` }}>
                        <colgroup>
                          {hasEditColumn && <col style={{ width: "40px" }} />}
                          {COLUMNS.map((f) => <col key={f.key} style={{ width: `${f.defW}px` }} />)}
                        </colgroup>
                        {/* Show header only for the first visible group */}
                        {key === grouped.find(g => !collapsedGroups[g.key])?.key && (
                          <thead className="sticky top-0 z-20">
                            <tr className="bg-slate-50 border-b border-slate-200">
                              {hasEditColumn && <th className="px-1 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500 text-center bg-slate-50 sticky left-0 z-30 border-r-2 border-r-slate-300" style={{width:40}}>{t("customer.col_edit", "Edit")}</th>}
                              {COLUMNS.map((f) => {
                                const isSorted = sortKey === f.key;
                                // req #12 (iter110): some columns are
                                // explicitly NOT sortable (e.g. derived
                                // financial roll-ups). Render their label
                                // as a static span instead of a button.
                                const sortable = f.sortable !== false;
                                return (
                                <th key={f.key} className={`px-2 py-2 text-xs font-bold uppercase tracking-[0.1em] whitespace-nowrap select-none border-r border-slate-100 text-slate-500 bg-slate-50 ${f.align === "right" ? "text-right" : "text-left"} ${f.sticky && !hasEditColumn ? "sticky left-0 z-30 border-r-2 border-r-slate-300 bg-slate-50" : ""} ${f.sticky && hasEditColumn ? "sticky left-[40px] z-30 border-r-2 border-r-slate-300 bg-slate-50" : ""}`}>
                                  {sortable ? (
                                    <button type="button" onClick={() => handleSort(f.key)}
                                      data-testid={`customer-sort-${f.key}`}
                                      data-sort-active={isSorted ? sortDir : "off"}
                                      className={`inline-flex items-center gap-1 w-full ${f.align === "right" ? "justify-end" : "justify-start"} ${isSorted ? "text-slate-900" : "hover:text-slate-700"}`}
                                      title={isSorted ? `Sorted ${sortDir}` : "Click to sort"}>
                                      <span>{f.label}</span>
                                      <span className={isSorted ? "opacity-100" : "opacity-40"}>
                                        {isSorted ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                                      </span>
                                    </button>
                                  ) : (
                                    <span data-testid={`customer-col-${f.key}`}
                                      className={`inline-flex items-center gap-1 w-full ${f.align === "right" ? "justify-end" : "justify-start"} text-slate-500`}>
                                      {f.label}
                                    </span>
                                  )}
                                </th>
                              );})}
                            </tr>
                            {showFilters && (
                              <tr className="bg-slate-100/80 border-b border-slate-200">
                                {hasEditColumn && <td className="px-1 py-1 bg-slate-100 sticky left-0 z-30 border-r-2 border-r-slate-300" />}
                                {COLUMNS.map((f) => (
                                  <td key={f.key} className={`px-1 py-1 ${f.sticky && !hasEditColumn ? "sticky left-0 z-30 border-r-2 border-r-slate-300 bg-slate-100" : ""} ${f.sticky && hasEditColumn ? "sticky left-[40px] z-30 border-r-2 border-r-slate-300 bg-slate-100" : ""}`}>
                                    {f.filterType === "text" ? (
                                      <input data-testid={`filter-customer-${f.key}`} type="text" placeholder={t("customer.col_filter_placeholder", "Filter...")}
                                        value={filters[f.key]?.value || ""} onChange={(e) => updateFilter(f.key, e.target.value)}
                                        className="w-full h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
                                    ) : null}
                                  </td>
                                ))}
                              </tr>
                            )}
                          </thead>
                        )}
                        <tbody>
                          {items.map((cust, idx) => {
                            const isOdd = idx % 2 === 1;
                            const oddBg = isCA ? "bg-red-50/50" : "bg-blue-50/40";
                            const oddSticky = isCA ? "bg-red-50" : "bg-blue-50";
                            const dayName = getDayOfWeek(cust["Contact Date"]);
                            const answeredBy = (cust["Answered By"] || "").toLowerCase();
                            const canEdit = isSuperEditor || (userEmail && answeredBy === userEmail);
                            return (
                              <tr key={cust.id} data-testid={`customer-row-${cust.id}`}
                                onClick={() => openCustomer(cust)}
                                className={`border-b border-slate-100 transition-colors cursor-pointer ${isOdd ? oddBg : "bg-white"} ${cfg.hoverRow}`}>
                                {hasEditColumn && (
                                  <td onClick={(e) => e.stopPropagation()} className={`px-1 py-1.5 text-center sticky left-0 z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}`}>
                                    <div className="flex items-center justify-center gap-1">
                                      {canEdit ? (
                                        <button data-testid={`customer-edit-${cust.id}`}
                                          className={`inline-flex items-center justify-center w-6 h-6 rounded-sm border transition-colors text-slate-500 ${isCA ? "border-red-200 hover:bg-red-50 hover:text-[#D80621]" : "border-slate-200 hover:bg-slate-100 hover:text-blue-700"}`}>
                                          <PencilSimple size={13} />
                                        </button>
                                      ) : <span className="text-slate-300 text-[10px]">—</span>}
                                      <RecordButton customerId={cust.CustomerID || cust.id}
                                        customerName={cust["Company Name"] || ""} />
                                    </div>
                                  </td>
                                )}
                                {COLUMNS.map((f) => {
                                  let cellVal = formatCell(cust[f.key], f.format, f);
                                  if (f.key === "Answered By" && cust["Answered By Name"]) {
                                    cellVal = cust["Answered By Name"];
                                  }
                                  const isCompanyName = f.key === "Company Name";
                                  const showRotor = isCompanyName && cust.Compressor_Owner === "Y";
                                  const typeIcon = isCompanyName ? customerTypeIcon(cust.Type) : null;
                                  const dayBadgeCls = dayName ? (DAY_BADGE_STYLE[dayName] || "bg-slate-100 text-slate-600 border-slate-200") : "";
                                  return (
                                    <td key={f.key} className={`px-2 py-1.5 text-sm overflow-hidden text-ellipsis whitespace-nowrap border-r border-slate-50 ${
                                      f.align === "right" ? "text-right font-mono" : "text-left"
                                    } ${isCompanyName ? "font-semibold text-slate-900" : "text-slate-700"} ${
                                      f.format === "money" && f.key === "Credit" ? "text-green-700" : ""} ${f.format === "money" && f.key === "Debit" ? "text-red-600" : ""} ${
                                      f.format === "money" && f.key === "avg_quote" ? "text-slate-700 font-semibold" : ""} ${
                                      f.sticky && !hasEditColumn ? `sticky left-0 z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}` : ""} ${
                                      f.sticky && hasEditColumn ? `sticky left-[40px] z-10 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}` : ""}`}
                                      title={cust[f.key] != null ? String(cust[f.key]) : ""}>
                                      {isCompanyName ? (
                                        <span className="inline-flex items-center gap-1.5">
                                          {dayName && (
                                            <span data-testid={`day-badge-${cust.id}`}
                                              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${dayBadgeCls} shrink-0`}>
                                              {dayName}
                                            </span>
                                          )}
                                          {typeIcon && <typeIcon.Icon size={14} className={`${typeIcon.color} shrink-0`} title={typeIcon.title} />}
                                          {showRotor && <RotorIcon size={13} color={COUNTRY_COLOR[country]} className="shrink-0" />}
                                          <span className="truncate">{cellVal}</span>
                                        </span>
                                      ) : f.key === "avg_quote" && cust.quote_count > 0 ? (
                                        <span title={`${cust.quote_count} quote(s)`}>{cellVal}</span>
                                      ) : f.key === "avg_quote" ? (
                                        <span className="text-slate-300">—</span>
                                      ) : cellVal}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </div>
          </>
        )}
      </main>

      {/* Customer Detail Panel (responsive modal) */}
      {selectedCustomer && (
        <CustomerDetail customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} cfg={cfg} isCA={isCA} user={user} />
      )}

      {showAdmin && <AdminPanel user={user} onClose={() => setShowAdmin(false)} />}
      {showAddCustomer && (
        <AddCustomerModal
          user={user}
          country={country}
          isCA={isCA}
          selfService={false}
          onClose={() => setShowAddCustomer(false)}
          onCreated={(res) => {
            setShowAddCustomer(false);
            // Reload the current page so the new row appears, then auto-open
            // the new customer's detail panel.
            load();
            setTimeout(() => {
              openCustomer({ CustomerID: res.customer_id, "Company Name": "" });
            }, 600);
          }}
        />
      )}
    </div>
  );
}

function CustomerDetail({ customer, onClose, cfg, isCA, user }) {
  const c = customer;
  const t = useT();
  const ccy = (c.Currency || "USD").toUpperCase();
  const contactDate = c["Contact Date"] ? new Date(c["Contact Date"]).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [compressors, setCompressors] = useState([]);
  const [parts, setParts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [notes, setNotes] = useState([]);
  const [phoneLogs, setPhoneLogs] = useState([]);
  const [openQuote, setOpenQuote] = useState(null);
  const [openCompressor, setOpenCompressor] = useState(null);
  const [showSendFiles, setShowSendFiles] = useState(false);
  const [recordingsTick, setRecordingsTick] = useState(0);

  useEffect(() => {
    if (!c.CustomerID) {
      setLoadingPersons(false); setLoadingQuotes(false);
      return;
    }
    setLoadingPersons(true);
    fetchCustomerPersons(c.CustomerID).then(d => setPersons(d.persons)).catch(() => {}).finally(() => setLoadingPersons(false));
    setLoadingQuotes(true);
    fetchCustomerQuotes(c.CustomerID, 50).then(d => setQuotes(d.quotes || [])).catch(() => {}).finally(() => setLoadingQuotes(false));
    fetchCustomerCompressors(c.CustomerID).then(d => setCompressors(d.items || [])).catch(() => {});
    fetchCustomerPartsPurchased(c.CustomerID, { limit: 200 }).then(d => setParts(d.items || [])).catch(() => {});
    fetchCustomerJournal(c.CustomerID).then(d => setJournal(d.items || [])).catch(() => {});
    fetchCustomerNotes(c.CustomerID).then(d => setNotes(d.items || [])).catch(() => {});
    fetchCustomerPhoneLogs(c.CustomerID).then(d => setPhoneLogs(d.items || [])).catch(() => {});
    // iter150M — auto-prompt to record when the user landed here from
    // "Find customers near me". Only fires once per stash.
    try {
      const raw = sessionStorage.getItem("__field_tools_offer_record");
      if (raw) {
        const stash = JSON.parse(raw);
        if (stash?.customer_id === c.CustomerID && (Date.now() - (stash.at || 0)) < 5 * 60 * 1000) {
          sessionStorage.removeItem("__field_tools_offer_record");
          if (window.confirm("Start recording this conversation now?")) {
            // Click the existing record pill in the header.
            setTimeout(() => {
              const btn = document.querySelector(`[data-testid="customer-detail-record-${c.CustomerID}"]`);
              btn && btn.click();
            }, 200);
          }
        }
      }
    } catch { /* noop */ }
  }, [c.CustomerID]);

  const refreshQuotes = useCallback(() => {
    if (!c.CustomerID) return;
    fetchCustomerQuotes(c.CustomerID, 50).then(d => setQuotes(d.quotes || [])).catch(() => {});
  }, [c.CustomerID]);

  const refreshPersons = useCallback(() => {
    if (!c.CustomerID) return;
    fetchCustomerPersons(c.CustomerID).then(d => setPersons(d.persons || [])).catch(() => {});
  }, [c.CustomerID]);

  return (
    <div className="fixed inset-0 z-50" data-testid="customer-detail-panel">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 top-16 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[560px] md:max-h-[80vh] md:rounded-sm bg-white md:border md:border-slate-200 rounded-t-xl shadow-xl overflow-y-auto">
        {/* Header bar */}
        <div className={`sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b ${isCA ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
          <div className="flex items-center gap-2 flex-1 mr-2 min-w-0">
            <h3 className="text-base font-bold font-['Chivo'] text-slate-900 truncate">{c["Company Name"] || "-"}</h3>
            <AIProvenanceBadge entityType="customer" entityId={c.CustomerID} userEmail={user?.email} />
          </div>
          <button
            data-testid={`customer-send-files-btn-${c.CustomerID}`}
            onClick={() => setShowSendFiles(true)}
            title={t("customer.send_files_tooltip", "Send Public Files to this customer")}
            className="mr-2 inline-flex items-center gap-1 h-7 px-2 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800"
          >
            <PaperPlaneTilt size={11} weight="bold" /> Send Files
          </button>
          <RecordButton variant="pill"
            customerId={c.CustomerID}
            customerName={c["Company Name"] || ""}
            className="mr-2"
            testId={`customer-detail-record-${c.CustomerID}`}
            onSubmitted={() => setRecordingsTick((n) => n + 1)} />
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* People at this customer */}
          <PeopleSection
            persons={persons}
            loading={loadingPersons}
            customerId={c.CustomerID}
            companyName={c["Company Name"]}
            isCA={isCA}
            user={user}
            onChanged={refreshPersons}
          />

          {/* iter150M — Past in-person conversation recordings (auto-hides for non-field users / zero rows) */}
          <CustomerRecordingsSection
            customerId={c.CustomerID}
            customerName={c["Company Name"]}
            refreshKey={recordingsTick}
          />

          {/* Quotes & Invoices */}
          <QuotesSection quotes={quotes} loading={loadingQuotes} isCA={isCA} user={user} onQuoteMutated={refreshQuotes} />

          {/* Compressors / Final Products owned */}
          <CompressorsOwnedSection items={compressors} isCA={isCA} onOpenCompressor={(serial) => setOpenCompressor(serial)} />

          {/* Parts Purchased */}
          <PartsPurchasedSection items={parts} ccy={ccy} isCA={isCA} onOpenInvoice={(hid) => setOpenQuote({ QuoteInvoiceHeadingID: hid })} />

          {/* Journal entries — click row jumps to /quote/:id */}
          <JournalSection items={journal} ccy={ccy} isCA={isCA} />

          {/* iter149e — Phone-call history for this customer */}
          <PhoneLogsSection items={phoneLogs} isCA={isCA} />

          {/* Customer-specific Notes & files (filtered by role on the backend) */}
          <NotesListSection
            items={notes}
            isCA={isCA}
            customerId={c.CustomerID}
            onAddNote={async (text) => {
              await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(c.CustomerID)}/notes`,
                { action: "Note", explanation: text },
                { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
              );
              fetchCustomerNotes(c.CustomerID).then(d => setNotes(d.items || [])).catch(() => {});
            }}
          />

          {/* Contact info — side-by-side compact rows */}
          <DetailSection title={t("customer.section_contact_info", "Contact Info")} compact>
            <DetailRow icon={<CalendarBlank size={14} />} label={t("customer.detail_contact_date", "Contact Date")} value={contactDate} compact />
            <DetailRow label={t("customer.detail_answered_by", "Answered By")} value={c["Answered By Name"] || c["Answered By"]} compact />
            <DetailRow label="Type" value={c.Type} compact />
            <DetailRow label="Segment" value={c.Segment} compact />
          </DetailSection>

          <DetailSection title="Address" compact>
            <DetailRow icon={<MapPin size={14} />} label={t("customer.detail_bill_to_address", "Bill to Address")} value={c["Bill to Address"]} multiline />
          </DetailSection>

          <DetailSection title="Financials" compact>
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-sm p-3 ${isCA ? "bg-red-50" : "bg-blue-50"}`}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Credit ({ccy})</p>
                <p className="text-lg font-bold font-mono text-green-700">{c.Credit != null ? `${Number(c.Credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}</p>
              </div>
              <div className={`rounded-sm p-3 ${isCA ? "bg-red-50" : "bg-blue-50"}`}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Debit ({ccy})</p>
                <p className="text-lg font-bold font-mono text-red-600">{c.Debit != null ? `${Number(c.Debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}</p>
              </div>
            </div>
          </DetailSection>

          {(c._Notes || c["notes special"]) && (
            <DetailSection title={t("customer.detail_notes", "Notes (Customer table)")}>
              {c._Notes && <p className="text-sm text-slate-700">{c._Notes}</p>}
              {c["notes special"] && <p className="text-sm text-slate-500 mt-1">{c["notes special"]}</p>}
            </DetailSection>
          )}
        </div>
        {openQuote && <QuoteDetailModal headingId={openQuote.QuoteInvoiceHeadingID} onClose={() => { setOpenQuote(null); refreshQuotes(); }} user={user} />}
        {openCompressor && (
          <CompressorDetailModal
            serialId={openCompressor}
            customerId={c.CustomerID}
            isCA={isCA}
            user={user}
            onOpenInvoice={(hid) => setOpenQuote({ QuoteInvoiceHeadingID: hid })}
            onClose={() => setOpenCompressor(null)}
          />
        )}
        <SendPublicFilesModal
          open={showSendFiles}
          onClose={() => setShowSendFiles(false)}
          customerId={c.CustomerID}
          recipients={persons
            .map((p) => (p["Main Email"] || "").trim().toLowerCase())
            .filter(Boolean)
            .slice(0, 5)}
          title={`Send documents to ${c["Company Name"] || "customer"}`}
        />
      </div>
    </div>
  );
}

function DetailSection({ title, children, compact }) {
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{title}</h4>
      <div className={compact ? "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" : "space-y-1.5"}>{children}</div>
    </div>
  );
}

// ─── New Wave-A sections ────────────────────────────────────────────
function fmtMoney(v, ccy = "USD") {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (isNaN(n)) return "—";
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n); }
  catch { return `${n.toFixed(2)}`; }
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return String(d); }
}

function CompressorsOwnedSection({ items, isCA, onOpenCompressor }) {
  const t = useT();
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="customer-compressors-section">
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
        <Receipt size={11} weight="bold" /> Compressors / Final Products owned <span className="text-slate-500 normal-case tracking-normal">({items.length})</span>
      </h4>
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden`}>
        {items.map((p, idx) => (
          <button
            key={p.id}
            type="button"
            data-testid={`customer-compressor-row-${p.serial_number || p.id}`}
            onClick={() => onOpenCompressor && onOpenCompressor(p.serial_id || p.serial_numberID || `${p.serial_number}`)}
            className={`w-full text-left p-2 flex items-center gap-2 text-xs ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/50" : "bg-blue-50/50")} hover:bg-slate-50 border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
          >
            <div className="shrink-0 w-12 text-[10px] font-bold text-slate-500 text-center">
              {p.product_type ? p.product_type.slice(0, 4).toUpperCase() : "—"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-mono font-semibold text-slate-800 text-[11px]">SN {p.serial_number || "—"}</span>
                <span className="text-slate-700">{p.product_name || "—"}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-slate-500 text-[10px]">
                {p.hours != null && <span>{Number(p.hours).toLocaleString()} hrs</span>}
                {p.date_delivered && <span>delivered {fmtDate(p.date_delivered)}</span>}
                {p.invoice_number && <span>· INV #{p.invoice_number}</span>}
                {!p.invoice_number && p.invoice_date && <span>· {fmtDate(p.invoice_date)}</span>}
              </div>
            </div>
            <span className="text-slate-400 text-[10px]">{t("customer.view_detail", "View detail →")}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PartsPurchasedSection({ items, ccy, isCA, onOpenInvoice }) {
  const t = useT();
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="customer-parts-section">
      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
        <FileIcon size={11} weight="bold" /> Parts purchased <span className="text-slate-500 normal-case tracking-normal">({items.length})</span>
      </h4>
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden`}>
        {items.map((p, idx) => (
          <button
            key={p.ID}
            type="button"
            data-testid={`customer-part-row-${p.ID}`}
            onClick={() => p.heading_id && onOpenInvoice && onOpenInvoice(p.heading_id)}
            className={`w-full text-left p-2 text-xs ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/50" : "bg-blue-50/50")} hover:bg-slate-50 border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-slate-800 font-medium">{p.product_name || p.product_code || "—"}</span>
              {p.related_serial && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200" title={t("customer.serial_service_record_tooltip", "Service record for this serial")}>
                  SN {p.related_serial}
                </span>
              )}
              {p.product_class && <span className="text-[10px] text-slate-400">· {p.product_class}</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-slate-500 text-[10px]">
              <span>qty {Number(p.quantity || 0).toLocaleString()}</span>
              <span>· {fmtMoney(p.price, ccy)}</span>
              {p.invoice_number && <span>· INV #{p.invoice_number}</span>}
              {p.invoice_date && <span>· {fmtDate(p.invoice_date)}</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function JournalSection({ items, ccy, isCA }) {
  const navigate = useNavigate();
  if (!items || items.length === 0) return null;
  const totalCredit = items.reduce((s, e) => s + (Number(e.credit) || 0), 0);
  const totalDebit  = items.reduce((s, e) => s + (Number(e.debit)  || 0), 0);
  return (
    <div data-testid="customer-journal-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
          <FileText size={11} weight="bold" /> Journal Entries <span className="text-slate-500 normal-case tracking-normal">({items.length})</span>
        </h4>
        <span className="text-[10px] font-mono text-slate-500">
          <span className="text-green-700">+{fmtMoney(totalCredit, ccy)}</span> /
          <span className="text-red-600 ml-1">-{fmtMoney(totalDebit, ccy)}</span>
        </span>
      </div>
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden max-h-72 overflow-y-auto`}>
        {items.map((j, idx) => {
          // Each entry's `quote` field IS the QuoteInvoiceHeadingID — link to /quote/:id.
          const quoteId = (j.quote || j.heading_id || "").toString().trim();
          const clickable = !!quoteId;
          return (
            <button
              key={j.journal_id}
              type="button"
              data-testid={`customer-journal-row-${j.journal_id}`}
              onClick={() => clickable && navigate(`/quote/${encodeURIComponent(quoteId)}`)}
              disabled={!clickable}
              title={clickable ? `Open quote/invoice ${quoteId}` : "No linked quote"}
              className={`w-full text-left p-2 text-xs flex items-center gap-2 ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/50" : "bg-blue-50/50")} ${clickable ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
            >
              <div className="shrink-0 w-16 text-[10px] text-slate-500">{fmtDate(j.entry_date)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-slate-700 font-medium truncate">{j.entry_type || "—"}</span>
                  {j.status && j.status !== "N/A" && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">{j.status}</span>}
                  {j.invoice_number && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">INV #{j.invoice_number}</span>}
                </div>
                {j.explanation && <div className="text-[10px] text-slate-500 truncate mt-0.5">{j.explanation}</div>}
              </div>
              <div className="shrink-0 text-right text-[11px] font-mono">
                {Number(j.credit) > 0 && <div className="text-green-700">+{fmtMoney(j.credit, ccy)}</div>}
                {Number(j.debit) > 0 && <div className="text-red-600">-{fmtMoney(j.debit, ccy)}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * PhoneLogsSection (iter149e) — recent phone calls tied to a customer
 * via PhoneLogs.customerID. Uses the same compact row pattern as the
 * Journal section so the customer detail panel keeps a consistent feel.
 */
function PhoneLogsSection({ items, isCA }) {
  const [openId, setOpenId] = useState(null);
  if (!items || items.length === 0) return null;

  const fmtCallDate = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return String(s).slice(0, 16).replace("T", " ");
      return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return String(s).slice(0, 16).replace("T", " "); }
  };
  const fmtDur = (s) => {
    const n = Number(s);
    if (!n || n < 0) return "—";
    const m = Math.floor(n / 60); const sec = n % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
  };
  const directionTone = (d) =>
    (d || "").toLowerCase().includes("out")
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-blue-50 text-blue-700 border-blue-200";

  return (
    <div data-testid="customer-phone-logs-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
          <Phone size={11} weight="bold" /> Phone Logs <span className="text-slate-500 normal-case tracking-normal">({items.length})</span>
        </h4>
      </div>
      <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden max-h-72 overflow-y-auto`}>
        {items.map((row, idx) => {
          const id = row.id || row.call_sid || `pl-${idx}`;
          const isOpen = openId === id;
          const direction = row.direction || (row.call_type || "").toLowerCase();
          const otherParty = direction.toLowerCase().includes("out") ? row.to_num : row.from_num;
          return (
            <div key={id}
                 data-testid={`customer-phone-log-row-${id}`}
                 className={`text-xs ${idx % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/50" : "bg-blue-50/50")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}>
              <button type="button" onClick={() => setOpenId(isOpen ? null : id)}
                      className="w-full text-left p-2 flex items-center gap-2 hover:bg-slate-50">
                <div className="shrink-0 w-24 text-[10px] text-slate-500">{fmtCallDate(row.call_at)}</div>
                <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${directionTone(direction)}`}>
                  {direction || row.call_type || "call"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-slate-700 truncate">{otherParty || row.from_num || "—"}</span>
                    {row.caller_name && <span className="text-slate-500 truncate">· {row.caller_name}</span>}
                    {row.status && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${
                        (row.status || "").toLowerCase() === "completed"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {row.status}
                      </span>
                    )}
                  </div>
                  {row.summary && <div className="text-[10px] text-slate-500 truncate mt-0.5">{row.summary}</div>}
                </div>
                <div className="shrink-0 text-right text-[11px] font-mono text-slate-500">{fmtDur(row.duration)}</div>
                <CaretDown size={10} className={`shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div data-testid={`customer-phone-log-detail-${id}`}
                     className="px-3 pb-3 pt-1 text-[11px] text-slate-700 space-y-1.5 bg-slate-50 border-t border-slate-100">
                  {row.handled_by && <div><b>Handled by:</b> {row.handled_by}</div>}
                  {row.reason && <div><b>Reason:</b> {row.reason}</div>}
                  {row.outcome && <div><b>Outcome:</b> {row.outcome}</div>}
                  {row.sentiment && <div><b>Sentiment:</b> {row.sentiment}</div>}
                  {row.transcript && (
                    <details className="mt-1">
                      <summary className="text-indigo-700 cursor-pointer text-[10px] font-bold uppercase tracking-wider">Transcript</summary>
                      <pre className="whitespace-pre-wrap font-sans mt-1 p-2 bg-white border border-slate-200 rounded-sm text-[10px] leading-relaxed max-h-40 overflow-auto">{row.transcript}</pre>
                    </details>
                  )}
                  {row.recording_url && (
                    <audio controls preload="none" className="w-full mt-1" src={row.recording_url}>
                      Your browser does not support audio playback.
                    </audio>
                  )}
                  {row.related_sn && (
                    <div className="text-[10px] text-slate-500">Related SN: <span className="font-mono">{row.related_sn}</span></div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PeopleSection({ persons, loading, customerId, companyName, isCA, user, onChanged }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [videoTarget, setVideoTarget] = useState(null);    // { personId, personName }
  const [smsTarget, setSmsTarget] = useState(null);        // { personId, personName, personPhone }

  const submitNew = async (form) => {
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons`,
        form,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      setAdding(false);
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to save"); }
  };
  const submitEdit = async (pfcid, form) => {
    setError(""); setBusyId(pfcid);
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons/${encodeURIComponent(pfcid)}`,
        form,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      setEditingId(null);
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to save"); }
    finally { setBusyId(null); }
  };
  const removeContact = async (p) => {
    if (!window.confirm(`Delete ${p.Name}? This cannot be undone.`)) return;
    setError(""); setBusyId(p.PFCID);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerId)}/persons/${encodeURIComponent(p.PFCID)}`,
        { withCredentials: true, headers: user?.email ? { "X-User-Email": user.email } : {} }
      );
      onChanged && onChanged();
    } catch (e) { setError(e?.response?.data?.detail || e?.message || "Failed to delete"); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="customer-people-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">People{persons?.length ? <span className="text-slate-500 normal-case tracking-normal"> ({persons.length})</span> : null}</h4>
        {customerId && (
          <button
            type="button"
            onClick={() => { setAdding((x) => !x); setEditingId(null); setError(""); }}
            data-testid="customer-add-person-btn"
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${isCA ? "border-red-200 text-red-700 hover:bg-red-50" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
          >
            {adding ? "Cancel" : "+ Add contact"}
          </button>
        )}
      </div>
      {error && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-sm p-2 mb-2" data-testid="customer-people-error">{error}</div>}
      {adding && <PersonForm isCA={isCA} initial={{}} onCancel={() => setAdding(false)} onSubmit={submitNew} testIdPrefix="add-person" />}
      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Spinner size={14} className="animate-spin" /> Loading contacts...</div>
      ) : (!persons || persons.length === 0) ? (
        !adding && <p className="text-xs text-slate-400">{t("customer.no_contacts", "No contacts found")}</p>
      ) : (
        <div className="space-y-2">
          {persons.map((p) => editingId === p.PFCID ? (
            <PersonForm key={p.id} isCA={isCA} initial={p} onCancel={() => setEditingId(null)} onSubmit={(form) => submitEdit(p.PFCID, form)} testIdPrefix={`edit-person-${p.id}`} busy={busyId === p.PFCID} />
          ) : (
            <PersonRow key={p.id} p={p} isCA={isCA} companyName={companyName}
              customerId={customerId}
              onEdit={() => { setEditingId(p.PFCID); setAdding(false); setError(""); }}
              onDelete={() => removeContact(p)}
              onVideo={() => setVideoTarget({ personId: p.PFCID, personName: p.Name })}
              onSms={() => setSmsTarget({
                personId: p.PFCID, personName: p.Name,
                personPhone: p.Telephone || p.Telephone_Alternate || "",
              })}
              busy={busyId === p.PFCID}
            />
          ))}
        </div>
      )}
      <VideoCallModal
        open={!!videoTarget}
        onClose={() => setVideoTarget(null)}
        customerId={customerId}
        customerName={companyName}
        personName={videoTarget?.personName || ""}
        userEmail={user?.email}
      />
      <SmsChatPanel
        open={!!smsTarget}
        onClose={() => setSmsTarget(null)}
        customerId={customerId}
        customerName={companyName}
        personId={smsTarget?.personId}
        personName={smsTarget?.personName || ""}
        personPhone={smsTarget?.personPhone || ""}
      />
    </div>
  );
}

function PersonRow({ p, isCA, companyName, customerId, onEdit, onDelete, onVideo, onSms, busy }) {
  const t = useT();
  const callHref = toTelHref(p.Telephone, p.Extension) || toTelHref(p.Telephone_Alternate);
  const emailHref = p["Main Email"]
    ? `mailto:${p["Main Email"]}?subject=${encodeURIComponent(`Re: ${companyName || ""}`.trim())}`
    : null;
  const hasPhone = !!(p.Telephone || p.Telephone_Alternate);
  const [sendFilesOpen, setSendFilesOpen] = useState(false);
  const personEmail = (p["Main Email"] || "").trim();
  return (
    <div className={`rounded-sm border p-2.5 ${isCA ? "border-red-100 bg-red-50/30" : "border-blue-100 bg-blue-50/30"} ${busy ? "opacity-60" : ""}`}>
      {/* Header: name + tag chips. Wraps to next line if too many tags. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
        <span className="text-sm font-semibold text-slate-900 mr-1">{p.Name}</span>
        {p.IsPrimary === "Y" && (
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm ${isCA ? "text-[#D80621] bg-red-100" : "text-blue-600 bg-blue-100"}`}>Primary</span>
        )}
        {p.Purchasing === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Purchasing</span>}
        {p.Accounting === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Accounting</span>}
        {p.Service === "Y" && <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.5 rounded-sm">Service</span>}
      </div>
      {/* Action toolbar: separate row so it never gets clipped on the right. */}
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        {callHref && (
          <a href={callHref} onClick={(e) => e.stopPropagation()} data-testid={`person-call-btn-${p.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border ${isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-blue-300 text-blue-700 hover:bg-blue-100"}`}
            title={`Call ${p.Telephone || p.Telephone_Alternate || ""}`}>
            <Phone size={11} weight="fill" /> Call
          </a>
        )}
        {hasPhone && onSms && (
          <button onClick={(e) => { e.stopPropagation(); onSms(); }} data-testid={`person-sms-btn-${p.id}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            title={`Send SMS to ${p.Name}`}>
            <ChatCircle size={11} weight="fill" /> Text
          </button>
        )}
        {onVideo && (
          <button onClick={(e) => { e.stopPropagation(); onVideo(); }} data-testid={`person-video-btn-${p.id}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50"
            title={`Start video call with ${p.Name}`}>
            <VideoCamera size={11} weight="fill" /> Video
          </button>
        )}
        {emailHref && (
          <a href={emailHref} onClick={(e) => e.stopPropagation()} data-testid={`person-email-btn-${p.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border ${isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-blue-300 text-blue-700 hover:bg-blue-100"}`}
            title={`Email ${p["Main Email"]}`}>
            <Envelope size={11} weight="fill" /> Email
          </a>
        )}
        {p["Main Email"] && (
          <ResendManualButton email={p["Main Email"]} name={p.Name} isCA={isCA} />
        )}
        {personEmail && (
          <button
            onClick={(e) => { e.stopPropagation(); setSendFilesOpen(true); }}
            data-testid={`person-send-files-btn-${p.id}`}
            title={`Send ACC / US Air documents to ${p.Name}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm bg-blue-700 text-white hover:bg-blue-800"
          >
            <PaperPlaneTilt size={11} weight="fill" /> Docs
          </button>
        )}
        {p.PFCID && (
          <>
            <span className="ml-auto" />
            <button onClick={onEdit} data-testid={`person-edit-btn-${p.id}`}
              className="text-[10px] font-bold uppercase tracking-wider px-2 h-6 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50">
              {t("customer.person_edit", "Edit")}
            </button>
            <button onClick={onDelete} data-testid={`person-delete-btn-${p.id}`} disabled={busy}
              className="text-[10px] font-bold uppercase tracking-wider px-2 h-6 rounded-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">
              {t("customer.person_delete", "Delete")}
            </button>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        {p["Main Email"] && (
          <a href={emailHref} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2 break-all">
            <Envelope size={12} weight="fill" className="text-blue-600 shrink-0" />{p["Main Email"]}
          </a>
        )}
        {p.Telephone && (
          <a href={toTelHref(p.Telephone, p.Extension)} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2">
            <Phone size={12} weight="fill" className="text-blue-600" />{p.Telephone}{p.Extension ? ` x${p.Extension}` : ""}
          </a>
        )}
        {p.Telephone_Alternate && (
          <a href={toTelHref(p.Telephone_Alternate)} className="inline-flex items-center gap-1 text-blue-700 underline underline-offset-2">
            <Phone size={12} weight="fill" className="text-blue-600" />{p.Telephone_Alternate}
          </a>
        )}
      </div>
      <SendPublicFilesModal
        open={sendFilesOpen}
        onClose={() => setSendFilesOpen(false)}
        customerId={customerId}
        recipients={personEmail ? [personEmail] : []}
        title={`Send documents to ${p.Name}`}
      />
    </div>
  );
}

function ResendManualButton({ email, name, isCA }) {
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.stopPropagation();
    if (!email) return;
    setSending(true);
    try {
      const lang = (navigator.language || "en").slice(0, 2).toLowerCase();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/manual/share`, {
        email, role: "customer",
        lang: ["en", "fr", "es"].includes(lang) ? lang : "en",
        message: "",
        sender_name: "",
      }, { withCredentials: true });
      toast.success(`Manual sent to ${name || email}`, { description: email });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      toast.error(typeof detail === "string" ? detail : "Failed to send manual");
    } finally {
      setSending(false);
    }
  };
  return (
    <button onClick={send} disabled={sending}
      data-testid={`person-manual-btn-${email}`}
      className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-6 rounded-sm border disabled:opacity-50 ${
        isCA ? "border-red-300 text-red-700 hover:bg-red-100" : "border-violet-300 text-violet-700 hover:bg-violet-50"
      }`}
      title={`Resend the user manual to ${email}`}>
      {sending ? <Spinner size={11} className="animate-spin" /> : <BookOpen size={11} weight="fill" />} Manual
    </button>
  );
}


function PersonForm({ isCA, initial, onCancel, onSubmit, testIdPrefix, busy }) {
  const t = useT();
  const [form, setForm] = useState({
    name: initial?.Name || "",
    email: initial?.["Main Email"] || "",
    phone: initial?.Telephone || "",
    telephone_alternate: initial?.Telephone_Alternate || "",
    extension: initial?.Extension || "",
    is_primary: initial?.IsPrimary === "Y",
    purchasing: initial?.Purchasing === "Y",
    accounting: initial?.Accounting === "Y",
    service: initial?.Service === "Y",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  const accentBtn = isCA ? "bg-red-700 hover:bg-red-800" : "bg-blue-700 hover:bg-blue-800";
  const tint = isCA ? "border-red-200 bg-red-50/40" : "border-blue-200 bg-blue-50/40";
  return (
    <form onSubmit={submit} className={`rounded-sm border p-3 mb-2 ${tint}`} data-testid={`${testIdPrefix}-form`}>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("customer.person_full_name_placeholder", "Full name")} required maxLength={120} data-testid={`${testIdPrefix}-name`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none col-span-2" />
        <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" maxLength={200} data-testid={`${testIdPrefix}-email`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" maxLength={80} data-testid={`${testIdPrefix}-phone`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input type="tel" value={form.telephone_alternate} onChange={(e) => set("telephone_alternate", e.target.value)} placeholder={t("customer.person_alt_phone_placeholder", "Alt phone (optional)")} maxLength={80} data-testid={`${testIdPrefix}-altphone`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
        <input value={form.extension} onChange={(e) => set("extension", e.target.value)} placeholder="Ext." maxLength={8} data-testid={`${testIdPrefix}-ext`} className="text-xs p-1.5 rounded-sm border border-slate-200 bg-white outline-none" />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 mb-2">
        {[
          ["is_primary", "Primary"],
          ["purchasing", "Purchasing"],
          ["accounting", "Accounting"],
          ["service", "Service"],
        ].map(([k, label]) => (
          <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`${testIdPrefix}-${k}`} /> {label}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border border-slate-200 text-slate-500 hover:bg-white">{t("customer.modal_cancel", "Cancel")}</button>
        <button type="submit" disabled={busy || !form.name.trim()} data-testid={`${testIdPrefix}-save`} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm text-white ${busy ? "bg-slate-400" : accentBtn} disabled:opacity-60`}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function NotesListSection({ items, isCA, customerId, onAddNote }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await onAddNote(text.trim()); setText(""); setAdding(false); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="customer-notes-list-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
          <FileText size={11} weight="bold" /> Notes &amp; Files <span className="text-slate-500 normal-case tracking-normal">({items?.length || 0})</span>
        </h4>
        {customerId && (
          <button
            type="button"
            onClick={() => setAdding((x) => !x)}
            data-testid="customer-add-note-btn"
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${isCA ? "border-red-200 text-red-700 hover:bg-red-50" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
          >
            {adding ? "Cancel" : "+ Add note"}
          </button>
        )}
      </div>
      {adding && (
        <div className={`mb-2 p-2 rounded-md border ${isCA ? "border-red-200 bg-red-50/30" : "border-blue-200 bg-blue-50/30"}`}>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={t("customer.note_placeholder", "Quick note — AI will polish it for the CRM record…")}
            className="w-full text-xs p-2 rounded-sm border border-slate-200 bg-white outline-none resize-none min-h-[60px]"
            data-testid="customer-add-note-textarea"
            disabled={saving}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setAdding(false); setText(""); }} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50" disabled={saving}>{t("customer.modal_cancel", "Cancel")}</button>
            <button onClick={submit} disabled={!text.trim() || saving} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm ${saving ? "bg-slate-300 text-white" : (isCA ? "bg-red-700 text-white" : "bg-blue-700 text-white")}`} data-testid="customer-add-note-save">
              {saving ? "Saving…" : "Save + AI polish ✨"}
            </button>
          </div>
        </div>
      )}
      {(!items || items.length === 0) ? (
        !adding && <p className="text-[11px] text-slate-400 italic">{t("customer.no_notes", "No notes recorded yet.")}</p>
      ) : (
        <div className={`rounded-md border ${isCA ? "border-red-100" : "border-blue-100"} overflow-hidden max-h-72 overflow-y-auto`}>
          {items.map((n, i) => (
            <div
              key={`${n.kind}-${n.id || i}`}
              data-testid={`customer-note-row-${n.id || i}`}
              className={`p-2 text-xs ${i % 2 === 0 ? "bg-white" : (isCA ? "bg-red-50/50" : "bg-blue-50/50")} border-b last:border-b-0 ${isCA ? "border-red-100" : "border-blue-100"}`}
            >
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${n.kind === "file" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {n.kind === "file" ? (n.Type || "FILE") : (n.Action || "NOTE")}
                </span>
                {n.kind === "file" && n.visibility && (
                  <span className="text-[9px] text-slate-500">vis: {n.visibility}</span>
                )}
                {n.ai_explanation && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200">
                    AI ✨
                  </span>
                )}
              </div>
              {n.Explanation && <p className="text-slate-700 whitespace-pre-line">{n.Explanation}</p>}
              {n.ai_explanation && (
                <p className="text-[10px] text-violet-700 italic mt-1 bg-violet-50/60 px-2 py-1 rounded-sm border-l-2 border-violet-300">
                  {n.ai_explanation}
                </p>
              )}
              <div className="text-[10px] text-slate-500 mt-1">
                <span>by {n.created_by || "—"}</span>
                {n.date_created && <span> · {fmtDate(n.date_created)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon, label, value, multiline, compact }) {
  if (!value || value === "None") return null;
  if (compact && !multiline) {
    return (
      <div className="flex items-baseline gap-2 py-1 border-b border-slate-100 last:border-b-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0 min-w-[100px]">{label}</span>
        <span className="text-sm text-slate-800 truncate">{String(value)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      {icon && <span className="text-slate-400 mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <p className={`text-sm text-slate-800 ${multiline ? "" : "truncate"}`}>{String(value)}</p>
      </div>
    </div>
  );
}

function statusBadgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("sold") || s.includes("confirmed")) return "text-green-700 bg-green-100 border-green-200";
  if (s === "quote" || s === "estimate" || s === "preliminary") return "text-amber-700 bg-amber-100 border-amber-200";
  if (s.includes("cancel")) return "text-slate-500 bg-slate-100 border-slate-200 line-through";
  if (s.includes("return") || s.includes("warranty")) return "text-purple-700 bg-purple-100 border-purple-200";
  if (s.includes("arrived")) return "text-blue-700 bg-blue-100 border-blue-200";
  return "text-slate-600 bg-slate-100 border-slate-200";
}

function formatQuoteDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); }
  catch { return String(d); }
}

function QuotesSection({ quotes, loading, isCA, user, onQuoteMutated }) {
  const t = useT();
  const [openQuote, setOpenQuote] = useState(null);
  const count = quotes.length;
  const soldTotal = quotes
    .filter(q => (q.Status || "").toLowerCase().includes("sold"))
    .reduce((s, q) => s + (Number(q.total) || 0), 0);
  return (
    <div data-testid="customer-quotes-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
          <Receipt size={11} weight="bold" /> Quotes &amp; Invoices {count > 0 && <span className="text-slate-500 normal-case tracking-normal">({count})</span>}
        </h4>        {count > 0 && soldTotal > 0 && (
          <span className="text-[10px] font-mono text-green-700 font-semibold" title={t("customer.avg_quote_tooltip", "Sum of sold/confirmed quotes")}>
            ${soldTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} sold
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Spinner size={14} className="animate-spin" /> Loading quotes...</div>
      ) : count === 0 ? (
        <p className="text-xs text-slate-400">{t("customer.no_quotes", "No quotes or invoices found")}</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {quotes.map((q) => {
            const isInvoice = !!q.invoice_number;
            const dateLabel = isInvoice ? q.invoice_date : q.quote_date;
            return (
              <button key={q.QuoteInvoiceHeadingID} type="button"
                data-testid={`quote-row-${q.QuoteInvoiceHeadingID}`}
                onClick={() => setOpenQuote(q)}
                className={`w-full text-left rounded-sm border p-2 flex items-center gap-2 text-xs transition-colors hover:shadow-sm ${isCA ? "border-red-100 bg-red-50/20 hover:bg-red-50/60" : "border-blue-100 bg-blue-50/20 hover:bg-blue-50/60"}`}>
                <div className="shrink-0 w-10 text-[10px] font-bold text-slate-500 text-center">
                  {isInvoice ? <FileText size={14} weight="fill" className="mx-auto text-blue-600" /> : <Receipt size={14} weight="bold" className="mx-auto text-amber-600" />}
                  <div className="mt-0.5">{isInvoice ? "INV" : "QUO"}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono font-semibold text-slate-800 text-[11px]">
                      {isInvoice ? `#${q.invoice_number}` : q.QuoteInvoiceHeadingID.split("__")[0]}
                    </span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${statusBadgeClass(q.Status)}`}>
                      {q.Status || "—"}
                    </span>
                    {q.Q_Type && q.Q_Type !== "Sales" && (
                      <span className="text-[9px] font-medium text-slate-500 bg-white border border-slate-200 px-1 py-0.5 rounded-sm">{q.Q_Type}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-slate-500 text-[10px]">
                    <span>{formatQuoteDate(dateLabel)}</span>
                    {q.po_number && <span>PO: {q.po_number}</span>}
                    {q.prepared_by && <span className="truncate">· {String(q.prepared_by).split("@")[0]}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono font-bold text-slate-900 text-[12px]">
                    {q.total != null ? `$${Number(q.total).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}` : "—"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {openQuote && <QuoteDetailModal headingId={openQuote.QuoteInvoiceHeadingID} onClose={() => { setOpenQuote(null); onQuoteMutated && onQuoteMutated(); }} user={user} />}
    </div>
  );
}

const SUPER_EDITORS_FE = SUPER_EDITOR_EMAILS;
const QUOTE_STATUS_OPTIONS = [
  "Quote", "Preliminary", "Estimate", "Sold & Order Confirmed", "Alternative Sold",
  "Warranty", "Return Ticket", "Goods Arrived", "Cancelled"
];

function QuoteDetailModal({ headingId, onClose, user }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const [filesFor, setFilesFor] = useState(null); // { ID, "Product Name", _ComputedKey }
  const audience = user?.role === "customer" ? "customer" : "all";

  const reload = useCallback(() => {
    return fetchQuote(headingId)
      .then((d) => { setData(d); setError(null); return d; })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load quote"));
  }, [headingId]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [reload]);

  const h = data?.heading;
  const isInvoice = !!h?.invoice_number;
  const userEmail = (user?.email || "").toLowerCase();
  const isSuper = SUPER_EDITORS_FE.includes(userEmail);
  const isPreparer = userEmail && userEmail === (h?.prepared_by || "").toLowerCase();
  const canEdit = !!h && !isInvoice && (user?.is_admin || isSuper || isPreparer);

  const showError = (msg) => {
    setToast({ kind: "error", msg });
    setTimeout(() => setToast(null), 4000);
  };

  const saveHeading = async (patch) => {
    const key = Object.keys(patch)[0];
    setSavingField(key);
    try {
      await updateQuoteHeading(headingId, patch);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Save failed — see admin about DB permissions");
    } finally {
      setSavingField(null);
    }
  };

  const saveLine = async (detailId, patch) => {
    setSavingField(`line:${detailId}`);
    try {
      await updateQuoteLine(headingId, detailId, patch);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Line update failed — check DB permissions");
    } finally {
      setSavingField(null);
    }
  };

  const removeLine = async (detailId) => {
    if (!window.confirm("Delete this line item?")) return;
    setDeletingId(detailId);
    try {
      await deleteQuoteLine(headingId, detailId);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.detail || "Delete failed — DB may lack DELETE permission");
    } finally {
      setDeletingId(null);
    }
  };

  const addLine = async (line) => {
    try {
      await addQuoteLine(headingId, line);
      await reload();
      setShowAdd(false);
    } catch (e) {
      showError(e?.response?.data?.detail || "Add failed");
    }
  };

  const title = loading
    ? "Loading..."
    : isInvoice
      ? `Invoice #${h.invoice_number}`
      : `Quote ${headingId.split("__")[0]}`;

  const subtotal = (data?.details || []).reduce(
    (s, d) => s + (Number(d.price) || 0) * (Number(d.Quantity) || 0) * (1 - (Number(d.discount) || 0)),
    0
  );

  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="quote-detail-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 top-8 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[820px] md:max-h-[90vh] md:rounded-sm bg-white md:border md:border-slate-300 rounded-t-xl shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2 min-w-0">
            {isInvoice ? <FileText size={18} weight="fill" className="text-blue-600 shrink-0" /> : <Receipt size={18} weight="bold" className="text-amber-600 shrink-0" />}
            <h3 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">{title}</h3>
            {h?.Status && !canEdit && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${statusBadgeClass(h.Status)}`}>{h.Status}</span>
            )}
            {isInvoice && <span className="text-[10px] font-bold uppercase text-slate-400">{t("customer.invoice_locked", "Invoice · locked")}</span>}
          </div>
          <button onClick={onClose} data-testid="quote-detail-close" className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {toast && (
          <div className={`sticky top-[52px] z-10 px-4 py-2 text-xs font-semibold ${toast.kind === "error" ? "bg-red-50 text-red-700 border-b border-red-200" : "bg-green-50 text-green-700"}`} data-testid="quote-toast">
            {toast.msg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Spinner size={24} className="animate-spin text-slate-400" /></div>
        ) : error ? (
          <div className="p-6 text-center text-red-600 text-sm">{error}</div>
        ) : !h ? null : (
          <div className="p-4 md:p-5 space-y-4">
            {/* Heading grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <HeadingField label={t("customer.quote_quote_date", "Quote Date")} value={formatQuoteDate(h.quote_date)} />
              {h.invoice_date && <HeadingField label={t("customer.quote_invoice_date", "Invoice Date")} value={formatQuoteDate(h.invoice_date)} />}
              {h.Q_Type && <HeadingField label="Type" value={h.Q_Type} />}
              {h.prepared_by && <HeadingField label={t("customer.quote_prepared_by", "Prepared By")} value={String(h.prepared_by).split("@")[0]} />}
              {canEdit ? (
                <EditableField label="Status" value={h.Status} onSave={(v) => saveHeading({ Status: v })} saving={savingField === "Status"} options={QUOTE_STATUS_OPTIONS} testid="edit-status" />
              ) : h.Status ? <HeadingField label="Status" value={h.Status} /> : null}
              {canEdit ? (
                <EditableField label={t("customer.quote_po_number", "PO Number")} value={h.po_number || ""} onSave={(v) => saveHeading({ po_number: v })} saving={savingField === "po_number"} testid="edit-po" />
              ) : h.po_number ? <HeadingField label={t("customer.quote_po_number", "PO Number")} value={h.po_number} /> : null}
              {canEdit ? (
                <EditableField label={t("customer.quote_validity_days", "Validity (days)")} value={h.Validity ?? ""} type="number" onSave={(v) => saveHeading({ Validity: Number(v) || 0 })} saving={savingField === "Validity"} testid="edit-validity" />
              ) : h.Validity ? <HeadingField label={t("customer.quote_valid_for", "Valid for")} value={`${h.Validity} days`} /> : null}
              {!isInvoice && h.validity_ends && <HeadingField label="Expires" value={formatQuoteDate(h.validity_ends)} />}
              {canEdit ? (
                <EditableField label="Payment" value={h.Payment || ""} onSave={(v) => saveHeading({ Payment: v })} saving={savingField === "Payment"} testid="edit-payment" />
              ) : h.Payment ? <HeadingField label="Payment" value={h.Payment} /> : null}
            </div>

            {/* Address */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{t("customer.quote_ship_to", "Ship To")}</p>
              {canEdit ? (
                <EditableTextarea value={h.address || ""} onSave={(v) => saveHeading({ address: v })} saving={savingField === "address"} testid="edit-address" placeholder="Address..." />
              ) : (
                <p className="text-sm text-slate-700 whitespace-pre-line">{h.address || "—"}</p>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("customer.quote_line_items", "Line Items")} ({data.detail_count})</p>
                {canEdit && !showAdd && (
                  <button onClick={() => setShowAdd(true)} data-testid="quote-add-line-btn"
                    className="text-[11px] font-semibold text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded-sm border border-blue-200">
                    {t("customer.quote_add_line_item", "+ Add line item")}
                  </button>
                )}
              </div>
              {showAdd && <AddLineForm onAdd={addLine} onCancel={() => setShowAdd(false)} />}
              <div className="border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr className="text-left border-b border-slate-200">
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] bg-slate-50">#</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] bg-slate-50">{t("table.product", "Product")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.qty", "Qty")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.unit_price_short", "Unit $")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.disc_pct", "Disc %")}</th>
                      <th className="px-2 py-1.5 font-semibold uppercase tracking-wider text-slate-500 text-[10px] text-right bg-slate-50">{t("table.line_total_short", "Line $")}</th>
                      {canEdit && <th className="px-1 py-1.5 w-7 bg-slate-50" />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.details.map((d, idx) => {
                      const qty = Number(d.Quantity) || 0;
                      const price = Number(d.price) || 0;
                      const disc = Number(d.discount) || 0;
                      const line = qty * price * (1 - disc);
                      const isSaving = savingField === `line:${d.QuoteInvoiceDetailID}`;
                      const isDeleting = deletingId === d.QuoteInvoiceDetailID;
                      return (
                        <tr key={d.QuoteInvoiceDetailID} data-testid={`quote-line-${d.QuoteInvoiceDetailID}`} className={`border-b border-slate-100 ${idx % 2 ? "bg-slate-50/50" : "bg-white"} ${isSaving || isDeleting ? "opacity-50" : ""}`}>
                          <td className="px-2 py-1.5 text-slate-400 font-mono text-[10px]">{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-start gap-1.5">
                              {d.part_id && (
                                <button data-testid={`quote-line-files-${d.QuoteInvoiceDetailID}`}
                                  onClick={() => setFilesFor({ ID: d.part_id, "Product Name": d.product_name, _ComputedKey: d.product_code })}
                                  title={t("customer.related_files", "Related files")}
                                  className="mt-0.5 w-5 h-5 inline-flex items-center justify-center rounded-sm border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-slate-500 shrink-0">
                                  <FileIcon size={11} />
                                </button>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900">{d.product_name || "—"}</div>
                                {d.explanation && <div className="text-[10px] text-slate-500 mt-0.5">{d.explanation}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={qty} step="1" min="0" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { quantity: Number(v) })} />
                            ) : <span className="font-mono">{qty}</span>}
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={price} step="0.01" min="0" prefix="$" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { price: Number(v) })} />
                            ) : <span className="font-mono">${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>}
                          </td>
                          <td className="px-1 py-1 text-right">
                            {canEdit ? (
                              <NumCell value={(disc * 100).toFixed(1)} step="0.5" min="0" max="100" onSave={(v) => saveLine(d.QuoteInvoiceDetailID, { discount: Math.max(0, Math.min(1, Number(v) / 100)) })} />
                            ) : <span className="font-mono text-slate-500">{disc > 0 ? `${(disc * 100).toFixed(1)}%` : "—"}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">${line.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          {canEdit && (
                            <td className="px-1 py-1 text-center">
                              <button onClick={() => removeLine(d.QuoteInvoiceDetailID)} data-testid={`delete-line-${d.QuoteInvoiceDetailID}`}
                                className="w-6 h-6 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-red-50 hover:text-red-600">
                                <X size={13} weight="bold" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {data.details.length === 0 && (
                      <tr><td colSpan={canEdit ? 7 : 6} className="px-2 py-4 text-center text-slate-400">{t("customer.no_line_items", "No line items.")} {canEdit && t("customer.click_add_line", "Click + Add line item to start.")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-full md:w-72 text-xs space-y-1">
                <TotalRow label="Subtotal" value={subtotal} />
                {canEdit ? (
                  <InlineTotalRow label="Shipping" value={h.Shipping} onSave={(v) => saveHeading({ Shipping: Number(v) })} saving={savingField === "Shipping"} />
                ) : h.Shipping > 0 && <TotalRow label="Shipping" value={h.Shipping} />}
                {canEdit ? (
                  <InlineTotalRow label={t("customer.quote_sales_tax", "Sales Tax")} value={h.sales_tax} onSave={(v) => saveHeading({ sales_tax: Number(v) })} saving={savingField === "sales_tax"} />
                ) : h.sales_tax > 0 && <TotalRow label={t("customer.quote_sales_tax", "Sales Tax")} value={h.sales_tax} />}
                {canEdit ? (
                  <InlineTotalRow label="Start-Up" value={h.start_up_charge} onSave={(v) => saveHeading({ start_up_charge: Number(v) })} saving={savingField === "start_up_charge"} />
                ) : h.start_up_charge > 0 && <TotalRow label="Start-Up" value={h.start_up_charge} />}
                <div className="flex justify-between border-t-2 border-slate-300 pt-1.5 mt-1">
                  <span className="font-bold text-slate-900 uppercase tracking-wide">Total</span>
                  <span className="font-mono font-bold text-lg text-slate-900">
                    ${Number(h.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{t("customer.quote_special_instructions", "Special Instructions")}</p>
              {canEdit ? (
                <EditableTextarea value={h.special_instructions || ""} onSave={(v) => saveHeading({ special_instructions: v })} saving={savingField === "special_instructions"} testid="edit-special" placeholder="Notes..." />
              ) : (
                <p className="text-xs text-slate-600 whitespace-pre-line">{h.special_instructions || "—"}</p>
              )}
            </div>
          </div>
        )}
      </div>
      {filesFor && <PartFilesModal part={filesFor} onClose={() => setFilesFor(null)} audience={audience} />}
    </div>,
    document.body
  );
}

function EditableField({ label, value, onSave, saving, options, type = "text", testid }) {
  const [draft, setDraft] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const commit = () => {
    if (String(draft) !== String(value ?? "")) onSave(draft);
    setFocused(false);
  };
  const baseClass = "w-full text-sm text-slate-800 font-medium bg-white border rounded-sm px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500";
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
        {label} {saving && <Spinner size={10} className="animate-spin text-blue-500" />}
      </p>
      {options ? (
        <select data-testid={testid} value={draft} onChange={(e) => { setDraft(e.target.value); onSave(e.target.value); }}
          className={`${baseClass} border-slate-300 focus:border-blue-500`}>
          {!options.includes(draft) && draft && <option value={draft}>{draft}</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input data-testid={testid} type={type} value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } if (e.key === "Escape") { setDraft(value ?? ""); e.currentTarget.blur(); } }}
          className={`${baseClass} ${focused ? "border-blue-500" : "border-slate-200 hover:border-slate-300"}`} />
      )}
    </div>
  );
}

function EditableTextarea({ value, onSave, saving, testid, placeholder }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = () => { if ((draft || "") !== (value || "")) onSave(draft); };
  return (
    <div className="relative">
      <textarea data-testid={testid} value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        className="w-full text-sm text-slate-700 bg-white border border-slate-200 rounded-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300 resize-y"
        rows={Math.min(6, Math.max(2, (draft || "").split("\n").length))} />
      {saving && <Spinner size={12} className="animate-spin text-blue-500 absolute top-2 right-2" />}
    </div>
  );
}

function NumCell({ value, step, min, max, prefix, onSave }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => { if (draft !== String(value)) onSave(draft); };
  return (
    <div className="relative inline-block">
      {prefix && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{prefix}</span>}
      <input type="number" value={draft} step={step} min={min} max={max}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setDraft(String(value)); e.currentTarget.blur(); } }}
        className={`w-20 text-right font-mono text-xs bg-white border border-slate-200 rounded-sm py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300 ${prefix ? "pl-4 pr-1.5" : "px-1.5"}`} />
    </div>
  );
}

function InlineTotalRow({ label, value, onSave, saving }) {
  const [draft, setDraft] = useState(String(value || 0));
  useEffect(() => { setDraft(String(value || 0)); }, [value]);
  const commit = () => { if (Number(draft) !== Number(value || 0)) onSave(Number(draft) || 0); };
  return (
    <div className="flex justify-between items-center text-slate-600">
      <span className="flex items-center gap-1">{label} {saving && <Spinner size={10} className="animate-spin text-blue-500" />}</span>
      <div className="relative">
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
        <input type="number" step="0.01" min="0" value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="w-24 text-right font-mono text-xs pl-4 pr-1.5 py-0.5 bg-white border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300" />
      </div>
    </div>
  );
}

function AddLineForm({ onAdd, onCancel }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [disc, setDisc] = useState("0");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2 || selected) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchParts(query, 15)
        .then(d => setResults(d.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected]);

  const pick = (r) => {
    setSelected(r);
    setQuery(r.name);
    if (r.price_usd) setPrice(String(r.price_usd));
    setResults([]);
  };

  const submit = () => {
    if (!selected || !qty || Number(qty) <= 0) return;
    onAdd({
      product_code: selected.code,
      quantity: Number(qty),
      price: Number(price) || 0,
      discount: (Number(disc) || 0) / 100,
    });
  };

  return (
    <div className="mb-2 p-3 border-2 border-dashed border-blue-200 rounded-sm bg-blue-50/30 space-y-2" data-testid="add-line-form">
      <div className="relative">
        <input type="text" placeholder={t("customer.parts_search_placeholder", "Search parts by name or code (min 2 chars)...")} value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          data-testid="add-line-search"
          className="w-full text-sm px-2 py-1.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
        {searching && <Spinner size={12} className="animate-spin absolute right-2 top-2 text-slate-400" />}
        {results.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-sm shadow-lg max-h-56 overflow-y-auto">
            {results.map((r) => (
              <button key={r.code} type="button" onClick={() => pick(r)}
                className="w-full text-left px-2 py-1 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-b-0 flex justify-between items-center gap-2">
                <span className="font-semibold text-slate-800 truncate">{r.name}</span>
                {r.price_usd != null && <span className="shrink-0 font-mono text-[11px] text-green-700">${Number(r.price_usd).toFixed(2)}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Qty</label>
            <input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)}
              className="w-20 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-qty" />
          </div>
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Unit $</label>
            <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              className="w-24 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-price" />
          </div>
          <div>
            <label className="text-[9px] uppercase font-bold text-slate-500 block">Disc %</label>
            <input type="number" min="0" max="100" step="0.5" value={disc} onChange={(e) => setDisc(e.target.value)}
              className="w-20 px-1.5 py-1 text-sm border border-slate-300 rounded-sm font-mono" data-testid="add-line-disc" />
          </div>
          <div className="flex gap-1 ml-auto">
            <button onClick={onCancel} className="px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded-sm">{t("customer.modal_cancel", "Cancel")}</button>
            <button onClick={submit} data-testid="add-line-submit"
              className="px-3 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-sm">{t("customer.parts_modal_add", "Add")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HeadingField({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 font-medium truncate">{String(value)}</p>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="font-mono">${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
  );
}
